import { useState, useEffect, useCallback } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import {
  ShoppingCart, Package, ChevronLeft, ChevronRight, X,
  Search, Tag, Loader2, Plus, Minus, Trash2, CreditCard, Wallet, Gift
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CART_KEY = "mm_cart";

const etatConfig = {
  neuf: { label: "Neuf", color: "bg-blue-100 text-blue-800" },
  occasion: { label: "Occasion", color: "bg-amber-100 text-amber-800" },
  reconditionne: { label: "Reconditionné", color: "bg-green-100 text-green-800" },
  comme_neuf: { label: "Comme neuf", color: "bg-teal-100 text-teal-800" },
};

const getStoredCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); }
  catch { return []; }
};

const ClientPortalBoutique = () => {
  const [neufs, setNeufs] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("all");
  const [cart, setCart] = useState(getStoredCart);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [creditSolde, setCreditSolde] = useState(0);
  const [useCredit, setUseCredit] = useState(false);
  const [creditLoaded, setCreditLoaded] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  // Fetch credit balance
  useEffect(() => {
    const fetchCredit = async () => {
      try {
        const res = await API.get("/portal/credit");
        setCreditSolde(res.data.solde || 0);
        setCreditLoaded(true);
      } catch {
        setCreditLoaded(true);
      }
    };
    fetchCredit();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await API.get("/portal/boutique");
        setNeufs(res.data.neufs || []);
        setOccasions(res.data.occasions || []);
      } catch {
        toast.error("Erreur lors du chargement des produits");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const allProducts = [...neufs, ...occasions];

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        if (existing.quantite >= (product.quantite_stock || 1)) {
          toast.error("Stock maximum atteint");
          return prev;
        }
        return prev.map(i => i.product_id === product.id ? { ...i, quantite: i.quantite + 1 } : i);
      }
      return [...prev, {
        product_id: product.id,
        nom: product.nom,
        prix: product.prix_vente,
        photo: (product.photos || [])[0] || null,
        etat: product.etat,
        max_stock: product.quantite_stock || 1,
        quantite: 1
      }];
    });
    toast.success("Ajouté au panier");
  }, []);

  const updateQuantity = useCallback((productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id !== productId) return item;
      const newQte = item.quantite + delta;
      if (newQte < 1) return item;
      if (newQte > item.max_stock) { toast.error("Stock maximum atteint"); return item; }
      return { ...item, quantite: newQte };
    }));
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(i => i.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = cart.reduce((sum, item) => sum + item.prix * item.quantite, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantite, 0);
  const creditApplied = useCredit ? Math.min(creditSolde, cartTotal) : 0;
  const totalApayer = cartTotal - creditApplied;

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setCheckingOut(true);
    try {
      // If credit covers the full amount
      if (useCredit && totalApayer <= 0) {
        // Use credit only — no Stripe needed
        const creditRes = await API.post("/portal/credit/use", { montant: cartTotal });
        // Create sale with credit payment
        await API.post("/portal/cart/checkout-credit", {
          items: cart.map(i => ({ product_id: i.product_id, quantite: i.quantite })),
          credit_utilise: creditRes.data.montant_utilise
        });
        setCreditSolde(creditRes.data.nouveau_solde);
        clearCart();
        setUseCredit(false);
        toast.success("Commande confirmée ! Payée avec votre crédit.");
        setCartOpen(false);
        return;
      }
      
      // If partial credit + Stripe
      if (useCredit && creditApplied > 0) {
        const creditRes = await API.post("/portal/credit/use", { montant: creditApplied });
        setCreditSolde(creditRes.data.nouveau_solde);
        // Checkout the remaining amount via Stripe
        const res = await API.post("/portal/cart/checkout", {
          items: cart.map(i => ({ product_id: i.product_id, quantite: i.quantite })),
          credit_utilise: creditApplied
        });
        if (res.data.checkout_url) {
          clearCart();
          window.location.href = res.data.checkout_url;
        }
      } else {
        // No credit — full Stripe
        const res = await API.post("/portal/cart/checkout", {
          items: cart.map(i => ({ product_id: i.product_id, quantite: i.quantite }))
        });
        if (res.data.checkout_url) {
          clearCart();
          window.location.href = res.data.checkout_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du paiement");
    } finally {
      setCheckingOut(false);
    }
  };

  const filterProducts = (products) => {
    if (!search) return products;
    const s = search.toLowerCase();
    return products.filter(p =>
      p.nom?.toLowerCase().includes(s) ||
      p.marque?.toLowerCase().includes(s) ||
      p.reference?.toLowerCase().includes(s)
    );
  };

  const filteredNeufs = filterProducts(neufs);
  const filteredOccasions = filterProducts(occasions);
  const showNeufs = activeTab === "all" || activeTab === "neufs";
  const showOccasions = activeTab === "all" || activeTab === "occasions";

  const openDetail = (product) => { setSelectedProduct(product); setCurrentPhotoIdx(0); };
  const closeDetail = () => { setSelectedProduct(null); setCurrentPhotoIdx(0); };

  const getCartQte = (productId) => cart.find(i => i.product_id === productId)?.quantite || 0;

  const ProductCard = ({ product }) => {
    const etat = etatConfig[product.etat] || etatConfig.neuf;
    const photos = product.photos || [];
    const inCart = getCartQte(product.id);
    return (
      <Card
        className="overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 group"
        data-testid={`boutique-product-${product.id}`}
        onClick={() => openDetail(product)}
      >
        <div className="relative h-44 bg-secondary/20 flex items-center justify-center overflow-hidden">
          {photos.length > 0 ? (
            <img src={`${BACKEND}${photos[0]}`} alt={product.nom} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <Package className="h-12 w-12 text-muted-foreground/20" />
          )}
          <Badge className={`absolute top-2 left-2 text-xs ${etat.color}`}>{etat.label}</Badge>
          {inCart > 0 && (
            <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">{inCart}</div>
          )}
        </div>
        <CardContent className="p-3 space-y-1.5">
          <p className="font-semibold text-sm line-clamp-1">{product.nom}</p>
          {product.marque && <p className="text-xs text-muted-foreground">{product.marque}</p>}
          <div className="flex items-center justify-between pt-1">
            <span className="text-lg font-bold text-primary">{product.prix_vente?.toFixed(2)} EUR</span>
            <Button
              size="sm"
              data-testid={`add-cart-${product.id}`}
              onClick={(e) => { e.stopPropagation(); addToCart(product); }}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Panier
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-portal-boutique">
      {/* Header with cart button */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Boutique</h1>
          <p className="text-muted-foreground">Découvrez tous nos produits disponibles</p>
        </div>
        <Button
          variant={cartCount > 0 ? "default" : "outline"}
          className="relative shrink-0"
          onClick={() => setCartOpen(true)}
          data-testid="open-cart-btn"
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Panier
          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </Button>
      </div>

      {/* Location banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="boutique-location-banner">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-amber-600 shrink-0"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
        <p className="text-sm font-medium text-amber-900">À récupérer à la boutique de Bergevin dans le Super U, 97110 POINTE-À-PITRE</p>
          <p className="text-xs text-amber-800 mt-1">Lun, Mar, Jeu, Ven : 8h - 14h | Sam : 9h - 13h | Fermé Mer et Dim</p>
      </div>

      {/* Search & Tabs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Rechercher un produit..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="boutique-search" />
        </div>
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {[
            { key: "all", label: "Tous", count: neufs.length + occasions.length },
            { key: "neufs", label: "Neufs", count: neufs.length },
            { key: "occasions", label: "Bonnes affaires", count: occasions.length },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeTab === tab.key ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"}`} data-testid={`tab-${tab.key}`}>
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Occasions */}
      {showOccasions && filteredOccasions.length > 0 && (
        <section data-testid="section-occasions">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5 text-amber-600" />
            <h2 className="text-xl font-bold">Bonnes affaires</h2>
            <Badge variant="secondary">{filteredOccasions.length}</Badge>
          </div>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-3">
            {filteredOccasions.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Neufs */}
      {showNeufs && filteredNeufs.length > 0 && (
        <section data-testid="section-neufs">
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-5 w-5 text-blue-600" />
            <h2 className="text-xl font-bold">Produits neufs</h2>
            <Badge variant="secondary">{filteredNeufs.length}</Badge>
          </div>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-3">
            {filteredNeufs.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {filteredNeufs.length === 0 && filteredOccasions.length === 0 && (
        <div className="text-center py-20">
          <Package className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="font-medium text-lg">Aucun produit trouvé</p>
          <p className="text-sm text-muted-foreground">Essayez une autre recherche</p>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="product-detail-modal" onClick={closeDetail}>
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64 sm:h-80 bg-secondary/30 flex items-center justify-center">
              {(selectedProduct.photos || []).length > 0 ? (
                <>
                  <img src={`${BACKEND}${selectedProduct.photos[currentPhotoIdx]}`} alt={selectedProduct.nom} className="w-full h-full object-contain" />
                  {selectedProduct.photos.length > 1 && (
                    <>
                      <button onClick={() => setCurrentPhotoIdx(i => (i - 1 + selectedProduct.photos.length) % selectedProduct.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70" data-testid="photo-prev">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={() => setCurrentPhotoIdx(i => (i + 1) % selectedProduct.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70" data-testid="photo-next">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <Package className="h-20 w-20 text-muted-foreground/30" />
              )}
              <button onClick={closeDetail} className="absolute top-3 right-3 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/70" data-testid="close-modal">
                <X className="h-4 w-4" />
              </button>
              <Badge className={`absolute top-3 left-3 ${(etatConfig[selectedProduct.etat] || etatConfig.neuf).color}`}>
                {(etatConfig[selectedProduct.etat] || etatConfig.neuf).label}
              </Badge>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <h2 className="text-xl font-bold">{selectedProduct.nom}</h2>
                {selectedProduct.marque && <p className="text-sm text-muted-foreground">{selectedProduct.marque}</p>}
              </div>
              {selectedProduct.description && <p className="text-sm text-muted-foreground">{selectedProduct.description}</p>}
              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <p className="text-3xl font-bold text-primary">{selectedProduct.prix_vente?.toFixed(2)} EUR</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedProduct.quantite_stock > 1 ? `${selectedProduct.quantite_stock} en stock` : "Dernier exemplaire !"}
                  </p>
                </div>
                <Button size="lg" data-testid="modal-add-cart-btn" onClick={() => { addToCart(selectedProduct); closeDetail(); }}>
                  <Plus className="h-5 w-5 mr-2" />
                  Ajouter au panier
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-background shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            {/* Cart Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                <h2 className="text-lg font-bold">Mon panier</h2>
                {cartCount > 0 && <Badge variant="secondary">{cartCount} article{cartCount > 1 ? "s" : ""}</Badge>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => setCartOpen(false)} data-testid="close-cart-btn">
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ShoppingCart className="h-16 w-16 text-muted-foreground/20 mb-4" />
                  <p className="font-medium text-muted-foreground">Votre panier est vide</p>
                  <p className="text-sm text-muted-foreground mt-1">Ajoutez des produits depuis la boutique</p>
                  <Button variant="outline" className="mt-4" onClick={() => setCartOpen(false)}>Continuer mes achats</Button>
                </div>
              ) : (
                <>
                  {cart.map(item => (
                    <div key={item.product_id} className="flex gap-3 p-3 rounded-lg border bg-card" data-testid={`cart-item-${item.product_id}`}>
                      <div className="w-16 h-16 rounded-md bg-secondary/30 flex items-center justify-center overflow-hidden shrink-0">
                        {item.photo ? (
                          <img src={`${BACKEND}${item.photo}`} alt={item.nom} className="w-full h-full object-cover" />
                        ) : (
                          <Package className="h-6 w-6 text-muted-foreground/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm line-clamp-1">{item.nom}</p>
                        <p className="text-sm text-primary font-bold">{item.prix.toFixed(2)} EUR</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <button
                            onClick={() => updateQuantity(item.product_id, -1)}
                            className="h-7 w-7 rounded-md border flex items-center justify-center hover:bg-secondary disabled:opacity-50"
                            disabled={item.quantite <= 1}
                            data-testid={`qty-minus-${item.product_id}`}
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm font-medium w-6 text-center">{item.quantite}</span>
                          <button
                            onClick={() => updateQuantity(item.product_id, 1)}
                            className="h-7 w-7 rounded-md border flex items-center justify-center hover:bg-secondary disabled:opacity-50"
                            disabled={item.quantite >= item.max_stock}
                            data-testid={`qty-plus-${item.product_id}`}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                          <span className="text-xs text-muted-foreground ml-1">
                            = {(item.prix * item.quantite).toFixed(2)} EUR
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={clearCart} className="text-sm text-muted-foreground hover:text-destructive transition-colors" data-testid="clear-cart-btn">
                    Vider le panier
                  </button>
                </>
              )}
            </div>

            {/* Cart Footer */}
            {cart.length > 0 && (
              <div className="border-t p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Sous-total ({cartCount} article{cartCount > 1 ? "s" : ""})</span>
                  <span className="text-lg font-bold">{cartTotal.toFixed(2)} EUR</span>
                </div>

                {/* Credit section */}
                {creditLoaded && creditSolde > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">
                        Vous disposez d'un crédit de {creditSolde.toFixed(2)} EUR
                      </span>
                    </div>
                    {!useCredit ? (
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setUseCredit(true)} data-testid="use-credit-btn">
                          <Wallet className="h-4 w-4 mr-1" /> Utiliser mon crédit
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => setUseCredit(false)} data-testid="keep-credit-btn">
                          Conserver
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm text-emerald-700">
                          <span>Crédit appliqué</span>
                          <span className="font-bold">- {creditApplied.toFixed(2)} EUR</span>
                        </div>
                        <button onClick={() => setUseCredit(false)} className="text-xs text-emerald-600 hover:underline">Annuler le crédit</button>
                      </div>
                    )}
                  </div>
                )}

                <Separator />
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total à payer</span>
                  <span className="text-2xl font-bold">{totalApayer.toFixed(2)} EUR</span>
                </div>
                <Button
                  className="w-full h-12 text-base"
                  size="lg"
                  disabled={checkingOut}
                  onClick={handleCheckout}
                  data-testid="checkout-btn"
                >
                  {checkingOut ? (
                    <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Traitement en cours...</>
                  ) : totalApayer <= 0 ? (
                    <><Gift className="h-5 w-5 mr-2" /> Confirmer (payé par crédit)</>
                  ) : (
                    <><CreditCard className="h-5 w-5 mr-2" /> Payer {totalApayer.toFixed(2)} EUR</>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  {totalApayer > 0 ? "Paiement sécurisé par Stripe" : "Paiement par crédit boutique"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalBoutique;
